/**
 * 52px of chrome, and nothing in it that is not doing a job: where you are,
 * how to get anywhere, who else is here, and whether we are still live.
 */

import { CircleHelp, Menu, PanelLeft, Search } from 'lucide-react';
import { useLocation, useMatches, useNavigate, useSearchParams } from 'react-router-dom';

import { ConnectionDot, PresenceBar } from '@/components/app/PresenceBar';
import { ThemeToggle } from '@/components/app/ThemeToggle';
import { UserMenu } from '@/components/app/UserMenu';
import type { ShellIcon } from '@/components/app/Sidebar';
import { Breadcrumb, CreateMenu, IconButton, Kbd, Skeleton } from '@/components/ui';
import type { BreadcrumbItem, CreateKind } from '@/components/ui';
import { cn } from '@/lib/cn';
import { IS_MAC, SHORTCUTS_BY_ID } from '@/lib/keyboard';
import { useSavedViews } from '@/lib/queries';
import { useRealtime } from '@/lib/realtime';
import type { RealtimeStatus } from '@/lib/realtime';
import type { SessionResponse } from '@shared/types';

/* ────────────────────────────────────────────────────────────────────────────
   Route metadata
   ──────────────────────────────────────────────────────────────────────────── */

/** What a route declares about itself; read from `handle` in the router. */
export interface RouteHandle {
  title: string;
  /** Rendered as the crumb before the title. */
  parent?: { label: string; to: string };
  icon?: ShellIcon;
}

function isRouteHandle(value: unknown): value is RouteHandle {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as RouteHandle).title === 'string'
  );
}

/** The deepest route that describes itself wins. */
function useRouteHandle(): RouteHandle | null {
  const matches = useMatches();
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle;
    if (isRouteHandle(handle)) return handle;
  }
  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
   Search
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * A ⌘K trigger, not a field.
 *
 * It used to wear `fieldShell()` — a 420px bordered box on the same baseline as
 * every screen's own toolbar filter. Two identical-looking search boxes 19px
 * apart said nothing about which one filters the table below, and the bigger one
 * won first read over the view title. Borderless and unfilled, only the toolbar
 * input reads as an input; this reads as the shortcut it is.
 *
 * The chip says ⌘K, not `/`. A list surface owns `/` — it puts the caret in the
 * toolbar's own search box, which is what that box's chip and its
 * `aria-keyshortcuts` have always claimed — so this control advertising the same
 * key put two identical chips on one screen, 98px apart, naming a key only one
 * of them answers to. ⌘K is the key that opens the palette from anywhere,
 * including from inside a field, which is exactly what this trigger does.
 */
function SearchField({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
      aria-label="Search inquiries, contacts and companies"
      className={cn(
        'group flex h-[var(--h-control)] max-w-[260px] min-w-0 items-center gap-2',
        'rounded-[var(--radius-sm)] border-0 bg-transparent px-2 text-left text-base',
        // Named properties, not `transition-colors`: that list carries
        // `outline-color`, and the focus ring must arrive at --focus on the
        // frame focus lands rather than fading up from the trigger's own ink.
        'transition-[color,background-color,border-color] ease-[var(--ease-out)] duration-[var(--dur-fast)] hover:bg-[var(--surface-hover)]',
        'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
      )}
    >
      <Search className="size-3.5 shrink-0 text-faint" strokeWidth={1.75} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-muted transition-[color] ease-[var(--ease-out)] group-hover:text-secondary">
        Search
      </span>
      <Kbd keys={['mod', 'k']} size="sm" />
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Connection
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The dot said nothing. A green circle in the corner of a CRM could mean the
 * server is up, the user is available, or a record is healthy; a first-time
 * viewer read it as "unlabelled decoration" and the arbiter said so. One word
 * beside it, from the same store the dot reads, removes the guess.
 */
const CONNECTION_WORD: Record<RealtimeStatus, string> = {
  idle: 'Paused',
  connecting: 'Connecting',
  open: 'Live',
  reconnecting: 'Reconnecting',
  offline: 'Offline',
};

function ConnectionStatus() {
  const { status } = useRealtime();
  return (
    <span className="flex items-center gap-1">
      <ConnectionDot />
      {/* The dot's own button carries the full announcement; this is the
          visible half of the same label. */}
      <span aria-hidden="true" className="hidden text-xs text-muted lg:inline">
        {CONNECTION_WORD[status]}
      </span>
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Topbar
   ──────────────────────────────────────────────────────────────────────────── */

export interface TopbarProps {
  collapsed: boolean;
  /** Below `MOBILE_BREAKPOINT`: the rail is a drawer and this row sheds weight. */
  mobile?: boolean;
  onToggleSidebar: () => void;
  onOpenSearch: () => void;
  onOpenHelp: () => void;
  /** The global create. Every screen inherits it from here. */
  onCreate: (kind: CreateKind) => void;
  session: SessionResponse | undefined;
}

export function Topbar({
  collapsed,
  mobile = false,
  onToggleSidebar,
  onOpenSearch,
  onOpenHelp,
  onCreate,
  session,
}: TopbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  /* Does anything on this route already emit an <h1>? `PageHeader` does, and
     the routes that mount it say so in their handle. See the note below. */
  const headingMatches = useMatches();
  const HeadingTag = headingMatches.some(
    (match) => (match.handle as { titled?: boolean } | undefined)?.titled === true,
  )
    ? 'span'
    : 'h1';
  const [params] = useSearchParams();
  const handle = useRouteHandle();
  const views = useSavedViews();

  // A saved view is a location too — the crumb should say which one you are in.
  const viewId = params.get('view');
  const viewName = viewId ? views.data?.find((view) => view.id === viewId)?.name : undefined;

  const crumbs: BreadcrumbItem[] = [];
  if (handle?.parent) {
    const parent = handle.parent;
    crumbs.push({ id: 'parent', label: parent.label, onClick: () => navigate(parent.to) });
  }
  if (handle) {
    crumbs.push(
      viewName
        ? { id: 'section', label: handle.title, onClick: () => navigate(location.pathname) }
        : { id: 'section', label: handle.title },
    );
  }
  if (viewName) crumbs.push({ id: 'view', label: viewName });

  return (
    <header
      className={[
        'flex h-[var(--h-topbar)] shrink-0 items-center gap-3',
        'border-b border-line bg-[var(--surface-1)] px-3',
      ].join(' ')}
    >
      {/* One control, two jobs. On a phone the rail is a modal drawer, so this
          is a disclosure — a hamburger that says "navigation" — and on a desktop
          it is the width toggle it has always been. Same slot, same key (⌘\),
          different verb; the label and the icon say which. */}
      <IconButton
        size="sm"
        label={
          mobile
            ? collapsed
              ? 'Open navigation'
              : 'Close navigation'
            : collapsed
              ? 'Expand sidebar'
              : 'Collapse sidebar'
        }
        tooltipSide="bottom"
        aria-expanded={!collapsed}
        aria-haspopup={mobile ? 'dialog' : undefined}
        onClick={onToggleSidebar}
      >
        {mobile ? <Menu /> : <PanelLeft />}
      </IconButton>

      {/*
        A location AND, on four routes, the heading — docs/CROSS-SURFACE-DECISIONS.md D7.

        The claim that used to sit here — "Every screen mounts `PageHeader`,
        whose title is the document's one `<h1>`" — was false for /inbox,
        /board, /contacts and /companies, which mount none. So 319 contacts and
        180 companies shipped with ZERO headings: the `H` key found nothing and
        a screen-reader rotor came back empty on the two directory screens.

        Rather than give lists a title band they deliberately do not want (the
        object is the page, and a band costs fold), the crumb carries the
        heading on exactly the routes where nothing else does. Same pixels,
        same 13px/500/--text-secondary, same `aria-current` inside the same
        navigation landmark — only the tag changes. Where a route DOES mount
        `PageHeader` (marked `titled` in the router) it stays a `<span>`, which
        is what keeps "Companies, heading level 1" from being announced twice.
      */}
      {/* `shrink-0` holds the location at full length on a desktop, where there
          is room for it. At 320px a two-crumb record title ("Companies ›
          Aalto Rakennus Oy") pushed the account menu 24px off the right edge —
          so on a phone the crumb is the thing that gives, and it already
          truncates. */}
      <div className="flex min-w-0 shrink-0 items-center mobile:shrink">
        {crumbs.length > 1 ? (
          <Breadcrumb items={crumbs} />
        ) : crumbs.length === 1 ? (
          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex min-w-0 items-center">
              <li className="flex min-w-0 items-center">
                <HeadingTag
                  aria-current="page"
                  className="truncate px-1 text-base font-medium text-secondary"
                >
                  {crumbs[0]!.label}
                </HeadingTag>
              </li>
            </ol>
          </nav>
        ) : (
          <Skeleton w={96} h={12} rounded="xs" />
        )}
      </div>

      {/* `data-tour` marks the controls the onboarding tour spotlights. See
          `features/onboarding/TourOverlay` — it measures the real element. */}
      <div data-tour="search" className="flex min-w-0 flex-1 items-center">
        {/* The word and the ⌘K chip are what makes this read as a field rather
            than a button — and both are dead weight on a screen with no ⌘ and
            no room. What survives is the magnifier, which is the one part a
            phone reader is looking for. */}
        {mobile ? (
          <IconButton
            size="sm"
            label="Search inquiries, contacts and companies"
            tooltipSide="bottom"
            onClick={onOpenSearch}
          >
            <Search />
          </IconButton>
        ) : (
          <SearchField onOpen={onOpenSearch} />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {/* The workspace create. Deliberately *not* gold: a screen carries one
            `--accent` fill, and it belongs to that screen's own primary in its
            toolbar. Measured before this, /inbox showed `New` at 1148,13 and
            `Log inquiry` at 1320,109 — same fill, same `Plus`, same weight, same
            right edge, 96px apart, and the first item of this menu opens the
            same dialog `Log inquiry` does. Outlined, it reads as the chrome's
            create beside the other chrome controls; the menu is unchanged. */}
        <span data-tour="create" className="flex items-center">
          <CreateMenu
            onCreate={onCreate}
            size="sm"
            align="end"
            variant="outline"
            iconOnly={mobile}
          />
        </span>

        {/*
          What a phone drops, and why none of it is lost.

          `PresenceBar` is a row of avatars 26px apart that has no room to be a
          row; the theme switch and the shortcut sheet are both already items in
          the account menu two controls to the right — printing them here as
          well spent a third of a 390px bar on duplicates. The connection state
          is the one thing with no second home, so the DOT stays and only its
          word goes: it is the difference between "your data is live" and "you
          are looking at a snapshot", which is not chrome.
        */}
        {mobile ? (
          <ConnectionDot />
        ) : (
          <>
            <span aria-hidden="true" className="mx-1 h-4 w-px bg-[var(--line)]" />

            <PresenceBar selfId={session?.user.id ?? null} />
            <ConnectionStatus />

            <span aria-hidden="true" className="mx-1 h-4 w-px bg-[var(--line)]" />

            <ThemeToggle size="sm" />
            <IconButton
              size="sm"
              label="Keyboard shortcuts"
              tooltipSide="bottom"
              aria-keyshortcuts={SHORTCUTS_BY_ID.help.spec}
              onClick={onOpenHelp}
            >
              <CircleHelp />
            </IconButton>

            <span aria-hidden="true" className="mx-1 h-4 w-px bg-[var(--line)]" />
          </>
        )}

        <span data-tour="account" className="flex items-center">
          <UserMenu session={session} variant="avatar" side="bottom" align="end" />
        </span>
      </div>
    </header>
  );
}

/** Chrome-only top bar, shown while the session is still resolving. */
export function TopbarSkeleton() {
  return (
    <header
      aria-hidden="true"
      className="flex h-[var(--h-topbar)] shrink-0 items-center gap-3 border-b border-line bg-[var(--surface-1)] px-3"
    >
      <Skeleton w={26} h={26} rounded="sm" />
      <Skeleton w={88} h={14} rounded="xs" />
      <div className="flex min-w-0 flex-1">
        <Skeleton w={104} h={12} rounded="xs" />
      </div>
      <Skeleton w={72} h={26} rounded="sm" />
      <Skeleton w={26} h={26} rounded="sm" />
      <Skeleton w={26} h={26} rounded="sm" />
      <Skeleton w={26} h={26} rounded="full" />
    </header>
  );
}
